import { useCallback } from 'react';
import { useInstantSearch } from 'react-instantsearch';

import type { ExtendedUIState } from 'components/search/InstantSearch/types';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useSearchCardsContext } from 'contexts/searchCards';
import type { VirtualToggleName } from 'lib/filters';
import { VIRTUAL_TOGGLE_FILTERS } from 'lib/filters';

import { useVirtualToggle } from './useVirtualToggle';

type VirtualToggleActions = {
  toggle: (value?: any) => void;
  setState?: (value: any) => void;
  condition?: boolean;
};

export const useVirtualToggleManager = () => {
  const {
    setGameweekFilter,
    setDailyFilter,
    setProbablePitchersFilter,
    setFavoriteFilter,
    setWatchlistFilter,
    setCompetitionEligibilityFilter,
    setCustomDecksFilter,
    setNotInLineUpFilter,
    setPromotionalEvent,
    setStatsMode,
    setDuplicatedKey,
  } = useSearchCardsContext() ?? {};

  const { currentUser } = useCurrentUserContext();
  const { setIndexUiState } = useInstantSearch<ExtendedUIState>();

  const { toggle: toggleGameweekFilter } = useVirtualToggle<boolean>({
    name: VIRTUAL_TOGGLE_FILTERS.gameweekFilter.name,
  });
  const { toggle: toggleDailyFilter } = useVirtualToggle<boolean>({
    name: VIRTUAL_TOGGLE_FILTERS.dailyFilter.name,
  });
  const { toggle: toggleProbablePitchersFilter } = useVirtualToggle<boolean>({
    name: VIRTUAL_TOGGLE_FILTERS.probablePitchersFilter.name,
  });
  const { toggle: toggleFavoriteFilter } = useVirtualToggle<boolean>({
    name: VIRTUAL_TOGGLE_FILTERS.favoriteFilter.name,
  });
  const { toggle: toggleNotInLineupFilter } = useVirtualToggle<boolean>({
    name: VIRTUAL_TOGGLE_FILTERS.notInLineupFilter.name,
  });
  const { toggle: toggleCompetitionEligibilityFilter } =
    useVirtualToggle<boolean>({
      name: VIRTUAL_TOGGLE_FILTERS.competitionEligibilityFilter.name,
    });
  const { toggle: toggleUnstackedFilter } = useVirtualToggle<boolean>({
    name: VIRTUAL_TOGGLE_FILTERS.unstackedFilter.name,
  });
  const { toggle: toggleCustomDecksFilter } = useVirtualToggle<boolean>({
    name: VIRTUAL_TOGGLE_FILTERS.customDecksFilter.name,
  });
  const { toggle: togglePromotionalEventFilter } = useVirtualToggle<boolean>({
    name: VIRTUAL_TOGGLE_FILTERS.promotionalEventFilter.name,
  });
  const { toggle: toggleStatsModeFilter } = useVirtualToggle<boolean>({
    name: VIRTUAL_TOGGLE_FILTERS.statsModeFilter.name,
  });
  const { toggle: toggleWatchlistFilter } = useVirtualToggle<boolean>({
    name: VIRTUAL_TOGGLE_FILTERS.watchlistFilter.name,
  });
  const { toggle: toggleDuplicateFilter } = useVirtualToggle<string | false>({
    name: VIRTUAL_TOGGLE_FILTERS.duplicateFilter.name,
  });

  return useCallback(
    (
      virtualToggle: {
        [name: string]: string | boolean | string[] | undefined;
      },
      shouldSetUiState = true
    ) => {
      if (!virtualToggle) return;

      const actionsPerVirtualToggle: Record<
        VirtualToggleName,
        VirtualToggleActions
      > = {
        [VIRTUAL_TOGGLE_FILTERS.gameweekFilter.name]: {
          toggle: toggleGameweekFilter,
          setState: setGameweekFilter,
        },
        [VIRTUAL_TOGGLE_FILTERS.dailyFilter.name]: {
          toggle: toggleDailyFilter,
          setState: setDailyFilter,
        },
        [VIRTUAL_TOGGLE_FILTERS.probablePitchersFilter.name]: {
          toggle: toggleProbablePitchersFilter,
          setState: setProbablePitchersFilter,
        },
        [VIRTUAL_TOGGLE_FILTERS.favoriteFilter.name]: {
          toggle: toggleFavoriteFilter,
          setState: setFavoriteFilter,
          condition: !!currentUser,
        },
        [VIRTUAL_TOGGLE_FILTERS.notInLineupFilter.name]: {
          toggle: toggleNotInLineupFilter,
          setState: setNotInLineUpFilter,
          condition: !!currentUser,
        },
        [VIRTUAL_TOGGLE_FILTERS.competitionEligibilityFilter.name]: {
          toggle: toggleCompetitionEligibilityFilter,
          setState: setCompetitionEligibilityFilter,
        },
        [VIRTUAL_TOGGLE_FILTERS.unstackedFilter.name]: {
          toggle: toggleUnstackedFilter,
        },
        [VIRTUAL_TOGGLE_FILTERS.customDecksFilter.name]: {
          toggle: toggleCustomDecksFilter,
          setState: setCustomDecksFilter,
        },
        [VIRTUAL_TOGGLE_FILTERS.promotionalEventFilter.name]: {
          toggle: togglePromotionalEventFilter,
          setState: setPromotionalEvent,
        },
        [VIRTUAL_TOGGLE_FILTERS.statsModeFilter.name]: {
          toggle: toggleStatsModeFilter,
          setState: setStatsMode,
        },
        [VIRTUAL_TOGGLE_FILTERS.watchlistFilter.name]: {
          toggle: toggleWatchlistFilter,
          setState: setWatchlistFilter,
        },
        [VIRTUAL_TOGGLE_FILTERS.duplicateFilter.name]: {
          toggle: toggleDuplicateFilter,
          setState: setDuplicatedKey,
        },
      };

      Object.entries(virtualToggle).forEach(([name, value]) => {
        if (name in actionsPerVirtualToggle) {
          const { toggle, setState, condition } =
            actionsPerVirtualToggle[name as VirtualToggleName];

          if (condition ?? true) {
            toggle(value);
            setState?.(value);
          }
        }
      });
      if (shouldSetUiState) {
        setIndexUiState(oldState => ({
          ...oldState,
          virtualToggle: { ...oldState.virtualToggle, ...virtualToggle },
        }));
      }
    },
    [
      toggleGameweekFilter,
      setGameweekFilter,
      toggleDailyFilter,
      setDailyFilter,
      toggleProbablePitchersFilter,
      setProbablePitchersFilter,
      toggleFavoriteFilter,
      setFavoriteFilter,
      currentUser,
      toggleNotInLineupFilter,
      setNotInLineUpFilter,
      toggleCompetitionEligibilityFilter,
      setCompetitionEligibilityFilter,
      toggleUnstackedFilter,
      toggleCustomDecksFilter,
      setCustomDecksFilter,
      togglePromotionalEventFilter,
      setPromotionalEvent,
      toggleStatsModeFilter,
      setStatsMode,
      toggleWatchlistFilter,
      setWatchlistFilter,
      toggleDuplicateFilter,
      setDuplicatedKey,
      setIndexUiState,
    ]
  );
};
